import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { 
  Mail, 
  Send, 
  BarChart3, 
  FileText, 
  Plus,
  TrendingUp,
  Users,
  MessageSquare,
  Clock
} from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useNavigate } from 'react-router-dom';
import { glassCard, glassCardStatic } from '@/utils/admin-glass-styles';

const AdminEmailOverview: React.FC = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    totalSent: 0,
    openRate: 0,
    clickRate: 0,
    activeTemplates: 0
  });
  const [recentActivity, setRecentActivity] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchOverviewData();
  }, []);

  const fetchOverviewData = async () => {
    setLoading(true);
    try {
      // Fetch email stats
      const { data: logs } = await supabase
        .from('email_logs')
        .select('*')
        .eq('status', 'sent');

      const { data: opens } = await supabase
        .from('email_opens')
        .select('*');

      const { data: clicks } = await supabase
        .from('email_clicks')
        .select('*');

      const { data: templates } = await supabase
        .from('email_templates')
        .select('*')
        .eq('is_active', true);

      // Fetch recent campaigns
      const { data: campaigns } = await supabase
        .from('email_campaigns')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(5);

      const totalSent = logs?.length || 0;
      const totalOpened = opens?.length || 0;
      const totalClicked = clicks?.length || 0;
      const openRate = totalSent > 0 ? (totalOpened / totalSent) * 100 : 0;
      const clickRate = totalOpened > 0 ? (totalClicked / totalOpened) * 100 : 0;

      setStats({
        totalSent,
        openRate,
        clickRate,
        activeTemplates: templates?.length || 0
      });

      // Format recent activity
      const activity = campaigns?.map(campaign => ({
        type: 'campaign',
        title: campaign.name,
        description: `Sent to ${campaign.sent_count || 0} users`,
        time: new Date(campaign.created_at).toLocaleString(),
        status: campaign.status
      })) || [];

      setRecentActivity(activity);

    } catch (error) {
      console.error('Error fetching overview data:', error);
    } finally {
      setLoading(false);
    }
  };

  const statsCards = [
    {
      title: 'Total Emails Sent',
      value: stats.totalSent.toLocaleString(),
      icon: <Send className="h-4 w-4 text-blue-500" />,
      change: '+12.3%',
      changeType: 'increase'
    },
    {
      title: 'Open Rate',
      value: `${stats.openRate.toFixed(1)}%`,
      icon: <Mail className="h-4 w-4 text-green-500" />,
      change: '+2.1%',
      changeType: 'increase'
    },
    {
      title: 'Click Rate',
      value: `${stats.clickRate.toFixed(1)}%`,
      icon: <TrendingUp className="h-4 w-4 text-purple-500" />,
      change: '+5.2%',
      changeType: 'increase'
    },
    {
      title: 'Active Templates',
      value: stats.activeTemplates.toString(),
      icon: <FileText className="h-4 w-4 text-amber-500" />,
      change: '+2',
      changeType: 'increase'
    }
  ];

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Email Management</h1>
          <p className="text-muted-foreground">
            Manage email campaigns, templates, and view analytics
          </p>
        </div>
        <Button 
          className="gap-2"
          onClick={() => navigate('/admin/emails/campaigns')}
        >
          <Plus className="h-4 w-4" />
          New Campaign
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        {statsCards.map((stat, index) => (
          <Card key={index} className={glassCard}>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">{stat.title}</CardTitle>
              {stat.icon}
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stat.value}</div>
              <p className="text-xs text-muted-foreground">
                <span className={`inline-flex items-center ${
                  stat.changeType === 'increase' ? 'text-green-600' : 'text-red-600'
                }`}>
                  <TrendingUp className="h-3 w-3 mr-1" />
                  {stat.change}
                </span>
                {' '}from last month
              </p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Recent Activity */}
      <Card className={glassCardStatic}>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Clock className="h-5 w-5" />
            Recent Activity
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {recentActivity.length > 0 ? (
              recentActivity.map((activity, index) => (
                <div key={index} className="flex items-center justify-between p-3 rounded-lg border">
                  <div className="flex items-center gap-3">
                    {activity.type === 'campaign' ? (
                      <Send className="h-4 w-4 text-blue-500" />
                    ) : (
                      <FileText className="h-4 w-4 text-amber-500" />
                    )}
                    <div>
                      <p className="font-medium">{activity.title}</p>
                      <p className="text-sm text-muted-foreground">{activity.description}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-muted-foreground">{activity.time}</p>
                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs ${
                      activity.status === 'sent' 
                        ? 'bg-green-100 text-green-800' 
                        : activity.status === 'sending'
                        ? 'bg-yellow-100 text-yellow-800'
                        : 'bg-blue-100 text-blue-800'
                    }`}>
                      {activity.status}
                    </span>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                No recent activity. Create your first email campaign to get started.
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Quick Actions */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card className={`${glassCard} cursor-pointer`} onClick={() => navigate('/admin/emails/campaigns')}>
          <CardContent className="flex items-center justify-center p-6">
            <div className="text-center">
              <Send className="h-8 w-8 mx-auto mb-2 text-blue-500" />
              <p className="font-medium">Campaigns</p>
              <p className="text-sm text-muted-foreground">Create and manage email campaigns</p>
            </div>
          </CardContent>
        </Card>
        
        <Card className={`${glassCard} cursor-pointer`} onClick={() => navigate('/admin/emails/templates')}>
          <CardContent className="flex items-center justify-center p-6">
            <div className="text-center">
              <FileText className="h-8 w-8 mx-auto mb-2 text-amber-500" />
              <p className="font-medium">Templates</p>
              <p className="text-sm text-muted-foreground">Manage email templates</p>
            </div>
          </CardContent>
        </Card>
        
        <Card className={`${glassCard} cursor-pointer`} onClick={() => navigate('/admin/emails/logs')}>
          <CardContent className="flex items-center justify-center p-6">
            <div className="text-center">
              <MessageSquare className="h-8 w-8 mx-auto mb-2 text-green-500" />
              <p className="font-medium">Email History</p>
              <p className="text-sm text-muted-foreground">View email logs and history</p>
            </div>
          </CardContent>
        </Card>
        
        <Card className={`${glassCard} cursor-pointer`} onClick={() => navigate('/admin/emails/analytics')}>
          <CardContent className="flex items-center justify-center p-6">
            <div className="text-center">
              <BarChart3 className="h-8 w-8 mx-auto mb-2 text-purple-500" />
              <p className="font-medium">Analytics</p>
              <p className="text-sm text-muted-foreground">View email analytics and reports</p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AdminEmailOverview;